import React from 'react';
import TopNav from './TopNav';
import Footer from './Footer';
import useSEO from '../hooks/useSEO';

function CodeFirstMethodologyPaperPage({ logo }) {
  useSEO({
    title: 'Whitepaper: The Code-First Revolution in Decision Intelligence',
    description:
      'Why diagram-first modeling is breaking under modern enterprise pressure, and how a code-first methodology enables executable, auditable, AI-native digital twins.'
  });

  return (
    <div className="min-h-screen bg-white">
      <TopNav logo={logo} />

      <main className="max-w-4xl mx-auto px-8 py-16 md:py-24">
        <h1 className="text-3xl md:text-4xl font-medium text-gray-900 leading-tight tracking-tight mb-6">
          Whitepaper: The Code-First Revolution in Decision Intelligence
        </h1>

        <h2 className="text-xl font-medium text-gray-900 mb-3">Executive Summary</h2>
        <p className="text-gray-700 leading-relaxed mb-8">
          Traditional modeling tools are failing the modern enterprise. As organizations race to deploy AI and navigate global volatility, the legacy "diagram-first" approach has become a bottleneck, creating a gap between strategic vision and operational reality. This whitepaper explores the shift to a <span className="font-medium">Code-First Methodology</span> - an AI-native approach to system dynamics that treats business logic as software. By moving from static diagrams to executable, version-controlled code, leaders can finally build "living" digital twins that scale with their ambitions.
        </p>

        <hr className="border-gray-200 my-10" />

        <section className="mb-10">
          <h2 className="text-2xl font-medium text-gray-900 mb-4">The Crisis of Static Modeling</h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            For decades, system dynamics has relied on graphical user interfaces (GUIs) to build models. While these visual "boxes and arrows" are helpful for conceptualizing problems, they introduce significant friction in an enterprise environment:
          </p>
          <ul className="list-disc pl-6 space-y-2 text-gray-700 leading-relaxed">
            <li><span className="font-medium">The Manual Bottleneck:</span> Building complex models by hand is slow, often lagging behind the speed of the market.</li>
            <li><span className="font-medium">The Integration Gap:</span> Proprietary model files are often data silos that cannot easily connect to modern data warehouses or real-time APIs.</li>
            <li><span className="font-medium">Lack of Version Control:</span> Managing different strategic scenarios usually involves a mess of file names like <span className="italic">Strategy_v2_Final_Final.vpm</span>.</li>
          </ul>
        </section>

        <hr className="border-gray-200 my-10" />

        <section className="mb-10">
          <h2 className="text-2xl font-medium text-gray-900 mb-4">The Code-First Solution: Logic as Infrastructure</h2>
          <p className="text-gray-700 leading-relaxed mb-6">
            A code-first approach, powered by the <span className="font-medium">Validatable Model Standard (VMS)</span>, flips the script. Instead of drawing a diagram to generate a model, we use AI to translate strategic intent into Python code. The code becomes the single source of truth, and the diagrams are generated automatically from that logic.
          </p>

          <h3 className="text-xl font-medium text-gray-900 mb-3">Core Benefit 1: Engineering-Grade Rigor (Git-Ready)</h3>
          <p className="text-gray-700 leading-relaxed mb-3">
            By treating business models as code, organizations can apply the same rigorous standards to their strategy that they apply to their software.
          </p>
          <ul className="list-disc pl-6 space-y-2 text-gray-700 leading-relaxed mb-6">
            <li><span className="font-medium">Branching Scenarios:</span> Analysts can create branches for different what-if scenarios (e.g., <span className="italic">Reshoring_Supply_Chain</span> vs. <span className="italic">Global_Expansion</span>).</li>
            <li><span className="font-medium">Auditability:</span> Every change to a decision variable is tracked, timestamped, and attributed, providing a complete audit trail for stakeholders and regulators.</li>
          </ul>

          <h3 className="text-xl font-medium text-gray-900 mb-3">Core Benefit 2: Seamless Ecosystem Integration</h3>
          <p className="text-gray-700 leading-relaxed mb-3">
            Code-first models live within the modern data stack. Unlike legacy software, a Veydra-powered model is a first-class citizen in the Python ecosystem.
          </p>
          <ul className="list-disc pl-6 space-y-2 text-gray-700 leading-relaxed mb-6">
            <li><span className="font-medium">Real-Time Data Ingestion:</span> Models can be plumbed directly into Snowflake, Databricks, or live IoT feeds.</li>
            <li><span className="font-medium">Advanced Analytics:</span> Use professional-grade libraries for optimization, sensitivity analysis, and machine learning to refine model parameters automatically.</li>
          </ul>

          <h3 className="text-xl font-medium text-gray-900 mb-3">Core Benefit 3: Modular Complexity (Master Model Orchestration)</h3>
          <p className="text-gray-700 leading-relaxed mb-3">
            Legacy tools often result in spaghetti models where a single change can break the entire system. A code-first methodology utilizes a <span className="font-medium">Master Model Orchestrator</span> to link independent submodels.
          </p>
          <ul className="list-disc pl-6 space-y-2 text-gray-700 leading-relaxed mb-6">
            <li><span className="font-medium">Separation of Concerns:</span> Finance can own the Cash Flow submodel, while Operations owns the Inventory submodel.</li>
            <li><span className="font-medium">Interdependency Mapping:</span> The system automatically simulates how a change in one submodel ripples through the others, revealing hidden risks and downstream pressures.</li>
          </ul>

          <h3 className="text-xl font-medium text-gray-900 mb-3">Core Benefit 4: Automatic Strategic Alignment</h3>
          <p className="text-gray-700 leading-relaxed mb-3">
            The primary risk in modeling is logic drift - when the visual diagram shown to the board no longer matches the math running the simulation. In a code-first environment, <span className="font-medium">the diagram is the code.</span>
          </p>
          <ul className="list-disc pl-6 space-y-2 text-gray-700 leading-relaxed">
            <li><span className="font-medium">Total Transparency:</span> Every causal loop and stock-flow diagram is rendered dynamically from the underlying Python logic.</li>
            <li><span className="font-medium">Shared Language:</span> This provides a Glass Box where technical architects and non-technical executives can align on the same live sandbox.</li>
          </ul>
        </section>

        <hr className="border-gray-200 my-10" />

        <section className="mb-8">
          <h2 className="text-2xl font-medium text-gray-900 mb-4">Conclusion: Clarity Before Commitment</h2>
          <p className="text-gray-700 leading-relaxed">
            In the era of AI and high-velocity change, hoping for the best is not a strategy. The code-first revolution allows organizations to crash the model instead of the business. By adopting a code-first methodology, enterprises move from static reports to a <span className="font-medium">Dynamic Flight Simulator</span>, gaining the clarity they need to navigate complexity before the stakes are real.
          </p>
        </section>

        <div className="mt-10 flex flex-col sm:flex-row gap-4">
          <a
            href="/contact"
            className="inline-flex items-center justify-center bg-gray-900 text-white px-6 py-3 rounded text-sm font-medium hover:bg-gray-800 transition-colors"
          >
            Schedule a conversation
          </a>
          <a
            href="/resources"
            className="inline-flex items-center justify-center border border-gray-300 text-gray-700 px-6 py-3 rounded text-sm font-medium hover:border-gray-400 hover:text-gray-900 transition-colors"
          >
            Back to Resources
          </a>
        </div>
      </main>

      <Footer logo={logo} />
    </div>
  );
}

export default CodeFirstMethodologyPaperPage;